import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { ResourceService } from "./resource.service";
import { ProcessModel } from "../model/process.model";
import { Router } from "@angular/router";
import { PagingContainerModel } from "../model/paging-container.model";
import { Observable, Subject } from "rxjs";
import { map, concatMap, mergeMap } from "rxjs/operators";
import { GlobalSettings } from "../model/settings.model";
import { InstanceService } from "./instance.service";
import { AuthenticationService } from "./auth.service";
import { SearchParamsModel } from "../model/search-params.model";
import { InstanceModel } from "../model/instance.model";
import { ValidationStrategyFactory } from "../strategy/validation-strategy.factory";
import { AttributeModel } from "../main/dynamic/model/attribute.model";
import { AttributeService } from "../main/dynamic/service/attribute.service";
import { MessageService } from "./message.service";
import { WorkflowGatewayService } from "./workflow-gateway.service";
import { MotorPolicyService } from "./motor-policy.service";

@Injectable({
    providedIn: 'root'
})
export class ProcessService extends ResourceService<ProcessModel> {

    private loadedSubject = new Subject<boolean>();

    processModel: ProcessModel
    policyAttributes: AttributeModel[]
    currentStep: InstanceModel

    constructor(
        http: HttpClient,
        settings: GlobalSettings,
        private attributeService: AttributeService,
        private router: Router,
        private instanceService: InstanceService,
        private motorPolicyService: MotorPolicyService,
        authService: AuthenticationService,
        private messageService: MessageService,
        private gatewayService: WorkflowGatewayService) {
        super(http, "process", settings.processosUrl, authService)
    }

    get isNew() : boolean {
        if(this.processModel && this.processModel.id) {
            return false
        } else {
            return true
        }
    }

    getVersion(name: string): Observable<ProcessModel> {
        return this.getWithComplement<ProcessModel>(`${this.processModel.id}/Versions/${name}`)
            .pipe(
                map((version) => {
                    let processModel = version
                    processModel.id = this.processModel.id
                    processModel.policyId = this.processModel.policyId
                    processModel.policyVersionId = this.processModel.policyVersionId

                    this.loadSavedData(processModel.attributes)
                    return processModel
                })
            )
    }

    getLoadingNotification(): Observable<boolean> {
        return this.loadedSubject.asObservable();
    }

    load(policyAttributes: AttributeModel[]): Observable<ProcessModel> {
        this.policyAttributes = policyAttributes

        let processId = sessionStorage.getItem("processId")

        if (processId) {
            return this.instanceService.getByIdFull(processId)
                .pipe(
                    map((processModel: ProcessModel) => {
                        this.currentStep = processModel.currentStep
                        this.loadResult(processModel)
                        this.loadSavedData(processModel.attributes)
                        return processModel
                    })
                )
        } else {
            return this.motorPolicyService.getBy(sessionStorage.getItem('idPolicy'))
                .pipe(map((processModel: ProcessModel) => {
                    this.currentStep = processModel.currentStep
                    this.loadResult(processModel)
                    this.loadSavedData(processModel.attributes)
                    return processModel
                }))
        }
    }

    private loadResult(model: ProcessModel) {
        if (model)
            this.processModel = model
        else {
            this.processModel = new ProcessModel(
                sessionStorage.getItem('idPolicy'),
                sessionStorage.getItem('policyVersionId'))
        }
    }

    private loadSavedData(originAttrs: AttributeModel[]) {
        if(!originAttrs)
            return;

        originAttrs
            .filter(attr => attr.type.toLowerCase() == "section" &&
                            attr.attributes.length > 0)
            .forEach(attr => {
                this.policyAttributes.push(this.attributeService.copy(attr))
                let message = this.getMessage('fases', attr)
                if(message){
                    this.messageService.sendMessage(message)
                }
            })
    }

    getMessage(name: string,savedSection: AttributeModel): string {
        if(savedSection.attributes){
            let fases = savedSection.attributes.find(a => a.key.toLowerCase() == name)
            if(fases && fases.attributes.length > 0){
                let items = fases.attributes.find(s => s.type == 'items')
                if(items && items.attributes.length > 0){
                    return items.attributes[0].key.replace(/ /g, "")
                }
            }
        }
    }

    isFirstStepEqualsCurrent(): boolean {
        if(this.currentStep && this.processModel.instance) {
            return this.currentStep.id == this.processModel.instance.id
        }

        return false
    }

    saveAttribute(attribute: AttributeModel): Observable<any> {
        if (this.processModel) {
            return this.send(attribute);
        }
    }

    private send(attribute: AttributeModel): Observable<any> {
        if (!this.processModel.id) {
            let attributeCopy = this.attributeService.copyCleanForSave(attribute)

            let foundIndex = this.processModel.attributes
                .findIndex(a => a.key === attributeCopy.key && a.type == attributeCopy.type)

            if (foundIndex > -1) {
                this.processModel.attributes.splice(foundIndex, 1, attributeCopy)
            } else {
                this.processModel.attributes.push(attributeCopy)
            }

            return this.gatewayService.create(this.processModel)
                .pipe(
                    map((value: ProcessModel) => {
                        this.processModel.id = value.id
                        sessionStorage.setItem("processId", value.id)
                        return value
                    })
                )
        } else {
            let attributeCopy = this.attributeService.copyExtraCleanForSave(attribute)

            return this.instanceService.update([attributeCopy], this.processModel.id)
        }
    }

    saveCurrentState(processId: string): Observable<any> {
        return this.patchWithComplement(`${processId}/Versions/Current`, {})
    }

    deleteFile(fileId: string): Observable<any> {
        return this.deleteWithComplement(`${encodeURIComponent(String(this.processModel.id))}/Files`, fileId);
    }

    getFile(query: string): Observable<any> {
        return this.getWithComplement(query);
    }

    resetAttributes() {
        this.processModel.attributes = []
    }

    newProcess(policyId: string) {
        sessionStorage.removeItem("processId")
        this.resetModel()
        this.router.navigate(["/processes/process/edit/" + policyId])
    }

    notifyLoaded() {
        this.loadedSubject.next(true);
    }

    resetModel() {
        this.processModel = null
        this.currentStep = null
        sessionStorage.removeItem('idPolicy')
        sessionStorage.removeItem("processId")
    }

    getList() {
        return this.get()
    }


    getSummary(pageNumber: number = 1,
        pageSize: number,
        attributes: AttributeModel[],
        options?: {
            searchParams?: SearchParamsModel
        }) {

        let params = new HttpParams()
            .set("PageNumber", String(pageNumber))
            .set("PageSize", String(pageSize))

        let searchBody = {
            policy: attributes,
            processesIds: []
        }

        if (options && options.searchParams) {
            Object.keys(options.searchParams).forEach(function (key) {
                if (key != "processesIds" && key != "filter" && key != "roles" && key != "attributes" &&
                    options.searchParams[key] &&
                    options.searchParams[key] != "") {
                    params = params.set(key, options.searchParams[key]);
                }
            });

            params = this.buildAttributeQueryParams(this.getFilterAttributes(options.searchParams), params)

            searchBody.processesIds = options.searchParams.processesIds
        }

        let query = `Search?${params.toString()}`

        return this.postWithComplement<PagingContainerModel, any>(query, searchBody)
    }

    getExcel(pageNumber: number = 1,
        pageSize: number = 10,
        attributes: AttributeModel[],
        options?: {
            userName?: string,
            filterName?: string,
            searchParams?: SearchParamsModel
        }) {

        let params = new HttpParams()
            .set("PageNumber", String(pageNumber))
            .set("PageSize", String(pageSize))

        if (options) {
            if (options.searchParams) {
                Object.keys(options.searchParams).forEach(function (key) {
                    if (key != "processesIds" &&
                        options.searchParams[key] &&
                        options.searchParams[key] != "") {
                        params = params.set(key, options.searchParams[key]);
                    }
                });
            }

            if (options.searchParams.processesIds) {
                options.searchParams.processesIds.forEach(function (value) {
                    params = params.append("processesIds", value);
                });
            }

            if (options.filterName) {
                params = params.set("filterName", options.filterName)
            }

            params = this.buildAttributeQueryParams(this.getFilterAttributes(options.searchParams), params)
        }

        let query = `Summary?${params.toString()}`

        return this.postWithComplement<PagingContainerModel, AttributeModel[]>
            (query, attributes)
    }

    getSummaryDashboard(pageNumber: number = 1, ids: string[], attributes: AttributeModel[]) {
        return this.postWithComplement<PagingContainerModel, AttributeModel[]>
            (`Summary?PageNumber=${pageNumber}&PageSize=${environment.processPageSize}&processesIds=${JSON.stringify(ids)}`, attributes)
    }

    updatePolicy(processModel: ProcessModel, policyAttributes: AttributeModel[]) {
        this.policyAttributes = policyAttributes
        this.processModel = processModel
        this.currentStep = processModel.currentStep
        this.loadSavedData(this.processModel.attributes);
    }

    private updatePolicyAttributes(attributes: AttributeModel[]) {
        attributes.forEach(attr => {
            this.updatePolicyAttributes(attr.attributes)
        })
    }

    validateByKey(key: string): boolean {
        if (!this.policyAttributes) {
            return true
        }

        let attr = this.attributeService
            .findBy(key, this.policyAttributes)

        if (!attr) {
            return true
        }

        let strategy = ValidationStrategyFactory.create(attr)
        return strategy.validate()
    }

    validate(): boolean {
        if (!this.policyAttributes) {
            return true
        }

        let isValid = true

        this.policyAttributes.forEach(section => {
            let attributes: AttributeModel[] = section.attributes;

            for (const attribute of attributes) {
                let validator = ValidationStrategyFactory.create(attribute)

                let result = validator.validate()

                if (!result) {
                    isValid = false
                }
            }
        });

        return isValid
    }
}
